import { useState, useCallback, useEffect } from 'react';
import type { RoomLayout } from '../../types.ts';
import type { ChatMessage, ArchitectChatResult } from '../types.ts';
import { AVAILABLE_ARCHITECT_MODELS } from '../types.ts';

const STORAGE_KEY_MESSAGES = 'architect_chat_messages';
const STORAGE_KEY_MODEL = 'architect_chat_model';

const INITIAL_WELCOME_MESSAGE: ChatMessage = {
  id: 'init-msg',
  sender: 'architect',
  text: 'Dobrý den! Jsem Váš AI architekt interiéru. Jak Vám mohu pomoci s návrhem a uspořádáním nábytku v této místnosti? Můžeme probrat ergonomii, průchody i estetické ladění.',
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
};

function getStoredMessages(): ChatMessage[] {
  if (typeof window === 'undefined') return [INITIAL_WELCOME_MESSAGE];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_MESSAGES);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to parse stored chat messages from sessionStorage:', err);
  }
  return [INITIAL_WELCOME_MESSAGE];
}

function getStoredModel(): string {
  if (typeof window === 'undefined') return 'gemini-3.7-flash';
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY_MODEL);
    if (saved && AVAILABLE_ARCHITECT_MODELS.some(m => m.id === saved)) {
      return saved;
    }
  } catch (err) {
    console.warn('Failed to load stored model from sessionStorage:', err);
  }
  return 'gemini-3.7-flash';
}

interface UseArchitectChatProps {
  backendUrl: string;
  roomData: RoomLayout;
  onApplyPromptToOrchestrator: (promptText: string, autoRun?: boolean) => void;
}

export function useArchitectChat({
  backendUrl,
  roomData,
  onApplyPromptToOrchestrator
}: UseArchitectChatProps) {
  const [selectedModel, setSelectedModel] = useState<string>(getStoredModel);
  const [messages, setMessages] = useState<ChatMessage[]>(getStoredMessages);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Sync messages to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(messages));
    } catch (err) {
      console.warn('Failed to save chat messages to sessionStorage:', err);
    }
  }, [messages]);

  // Sync selectedModel to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_MODEL, selectedModel);
    } catch (err) {
      console.warn('Failed to save selected model to sessionStorage:', err);
    }
  }, [selectedModel]);

  const sendMessage = useCallback(async (text: string, activeSkillIds?: string[]) => {
    if (!text.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: text.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setErrorMessage(null);

    // Build chat history for Gemini API payload
    const historyPayload = messages
      .filter(m => m.id !== 'init-msg')
      .map(m => ({
        role: (m.sender === 'user' ? 'user' : 'model') as 'user' | 'model',
        text: m.text
      }));

    try {
      const response = await fetch(`${backendUrl}/api/architect-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: historyPayload,
          roomLayout: roomData,
          model: selectedModel,
          activeSkillIds
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Chyba API serveru (${response.status})`);
      }

      const result = (await response.json()) as ArchitectChatResult;

      const architectMsg: ChatMessage = {
        id: `arch-${Date.now()}`,
        sender: 'architect',
        text: result.userMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        action: result.action,
        targetTextareaValue: result.targetTextareaValue,
        modelUsed: selectedModel,
        usedSkills: result.usedSkills
      };

      setMessages(prev => [...prev, architectMsg]);

      // If action is EXECUTE_ORCHESTRATOR, fill target Textarea value
      if (result.action === 'EXECUTE_ORCHESTRATOR' && result.targetTextareaValue) {
        onApplyPromptToOrchestrator(result.targetTextareaValue, false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      setMessages(prev => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          sender: 'architect',
          text: `Omlouvám se, při komunikaci s AI došlo k chybě: ${msg}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, roomData, selectedModel, messages, isLoading, onApplyPromptToOrchestrator]);

  const clearChat = useCallback(() => {
    const resetMsg: ChatMessage = {
      id: `init-${Date.now()}`,
      sender: 'architect',
      text: 'Konverzace byla resetována. Jaké úpravy v místnosti byste chtěl navrhnout?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([resetMsg]);
    setErrorMessage(null);
    try {
      sessionStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify([resetMsg]));
    } catch (err) {
      console.warn('Failed to clear chat in sessionStorage:', err);
    }
  }, []);

  return {
    messages,
    selectedModel,
    setSelectedModel,
    availableModels: AVAILABLE_ARCHITECT_MODELS,
    isLoading,
    errorMessage,
    sendMessage,
    clearChat
  };
}
